import { Fragment, useEffect, useRef, useState } from 'react';
import { Pagination } from 'components';
import { GreyStrokeInfoIcon, SortByIcon } from 'assets/icons';
import classNames from 'classnames';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { getSortTooltipText } from 'containers/accounting/pay-runs/utils';
import { GenericTableProps, TableChildRow, TableChildRowVariant, TableColumn } from './types';
import { useStickyTableHeader } from './useStickyTableHeader';

const rightFixedColumnSeperatorStyles = `first-letter:before:content-[""] before:absolute before:top-0 
 before:-left-[14px] before:bottom-0 before:w-[14px] before:bg-gradient-to-l from-[#162a400f] 
 to-transparent before:border-r before:border-borderGray`;

const leftFixedColumnSeperatorStyles = `first-letter:after:content-[""] after:absolute after:top-0 after:-right-[14px] 
 after:bottom-0 after:w-[14px] after:bg-gradient-to-r from-[#162a400f] to-transparent after:border-l 
 after:border-borderGray`;

const GenericTable = <T extends Record<string, any>>({
  data,
  columns,
  pagination,
  sorting,
  selection,
  onRowClick,
  className = '',
  tableClassName = '',
  minHeight = 'auto',
  showCheckbox = false,
  enableRowHover = true,
  columnInfo,
  emptyState,
  loading = false,
  loadingComponent,
  heightCalculationElementIds,
  textColumns = [],
  highlightedRowId,
  stickyHorizontalScrollbar,
  wrapperClassName = '',
  rowGroups,
  childRows
}: GenericTableProps<T>) => {
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  const [hoveredTooltipId, setHoveredTooltipId] = useState<string | null>(null);
  const [tableMinHeight, setTableMinHeight] = useState<string>(minHeight);
  const [isStickyScrollbarHovered, setIsStickyScrollbarHovered] = useState(false);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [scrollbarRect, setScrollbarRect] = useState<{ left: number; width: number } | null>(null);
  const { tableWrapperRef, tableHeaderRef } = useStickyTableHeader();
  const stickyScrollbarRef = useRef<HTMLDivElement>(null);
  const isSyncingScrollRef = useRef(false);

  // Measure horizontal overflow to show sticky scrollbar only when needed
  useEffect(() => {
    if (!stickyHorizontalScrollbar) {
      setHasHorizontalOverflow(false);
      return;
    }

    const wrapper = tableWrapperRef.current;
    if (!wrapper) {
      setHasHorizontalOverflow(false);
      return;
    }

    const checkOverflow = () => {
      if (!tableWrapperRef.current) return;
      setHasHorizontalOverflow(tableWrapperRef.current.scrollWidth > tableWrapperRef.current.clientWidth);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });
    resizeObserver.observe(wrapper);

    return () => {
      resizeObserver.disconnect();
    };
  }, [stickyHorizontalScrollbar, data, columns, columnInfo, rowGroups]);

  // Calculate table min-height based on other elements
  useEffect(() => {
    if (minHeight !== 'auto' || !heightCalculationElementIds) {
      setTableMinHeight(minHeight);
      return;
    }

    const calculateTableHeight = () => {
      let totalHeight = 0;

      heightCalculationElementIds.forEach(id => {
        const element = document.getElementById(id);
        if (element) {
          const rect = element.getBoundingClientRect();
          totalHeight += rect.height;
        }
      });

      // Add some padding/margin buffer
      const buffer = 90;
      const calculatedMinHeight = `calc(100vh - ${totalHeight + buffer}px)`;
      setTableMinHeight(calculatedMinHeight);
    };

    calculateTableHeight();
    window.addEventListener('resize', calculateTableHeight);

    return () => {
      window.removeEventListener('resize', calculateTableHeight);
    };
  }, [data, minHeight, heightCalculationElementIds]);

  // Sync horizontal scroll between table wrapper and sticky scrollbar (both directions)
  useEffect(() => {
    if (!stickyHorizontalScrollbar || !tableWrapperRef.current || !stickyScrollbarRef.current) return;

    const tableWrapper = tableWrapperRef.current;
    const stickyScrollbar = stickyScrollbarRef.current;

    const handleTableScroll = () => {
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      stickyScrollbar.scrollLeft = tableWrapper.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    };

    const handleStickyScroll = () => {
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      tableWrapper.scrollLeft = stickyScrollbar.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    };

    tableWrapper.addEventListener('scroll', handleTableScroll);
    stickyScrollbar.addEventListener('scroll', handleStickyScroll);

    return () => {
      tableWrapper.removeEventListener('scroll', handleTableScroll);
      stickyScrollbar.removeEventListener('scroll', handleStickyScroll);
    };
  }, [stickyHorizontalScrollbar, data, hasHorizontalOverflow]);

  // Keep fixed scrollbar aligned with table (position at bottom of viewport)
  useEffect(() => {
    if (!stickyHorizontalScrollbar || !hasHorizontalOverflow) {
      setScrollbarRect(null);
      return;
    }

    const wrapper = tableWrapperRef.current;
    if (!wrapper) {
      setScrollbarRect(null);
      return;
    }

    const updateRect = () => {
      if (!tableWrapperRef.current) return;
      const rect = tableWrapperRef.current.getBoundingClientRect();
      setScrollbarRect({ left: rect.left, width: rect.width });
    };

    updateRect();

    const resizeObserver = new ResizeObserver(updateRect);
    resizeObserver.observe(wrapper);
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [stickyHorizontalScrollbar, hasHorizontalOverflow]);

  // Helper function to format cell values
  const formatCellValue = (
    item: T,
    accessor: TableColumn<T>['accessor'],
    rowContext?: { isChildRow?: boolean; rowVariant?: TableChildRowVariant; parentItem?: T }
  ) => {
    if (typeof accessor === 'function') {
      return accessor({
        ...item,
        selectedItems: selection?.selectedItems,
        onItemSelect: selection?.onItemSelect,
        ...rowContext
      });
    }

    // Check for null, undefined, or zero values
    const value = item[accessor as keyof T];
    return value != null && value !== '' && value !== 0 ? String(value) : '-';
  };

  const getChildRowBackgroundClass = (variant?: TableChildRowVariant, isHighlighted?: boolean): string => {
    if (isHighlighted) {
      return 'bg-secondaryBtn';
    }
    if (variant === 'dispute') {
      return 'bg-[#FBF1DF]';
    }
    if (variant === 'refund' || variant === 'return') {
      return 'bg-secondaryBtn';
    }
    return 'bg-white';
  };

  // Get visible columns based on columnInfo configuration
  const getVisibleColumns = (): TableColumn<T>[] => {
    let result = [...columns];

    if (columnInfo && columnInfo.length > 0) {
      const enabledColumnKeys = columnInfo.filter(col => col.isEnabled).map(col => col.columnKey);
      result = result.filter(
        col => enabledColumnKeys.includes(col.id) || (showCheckbox && col.id === 'checkbox') || col.id === 'actions'
      );

      const checkboxCol = result.find(col => col.id === 'checkbox');
      const actionsCol = result.find(col => col.id === 'actions');
      const otherCols = result.filter(col => !['actions', 'checkbox'].includes(col.id));

      const columnsToSort = [...otherCols];
      columnsToSort.sort((a, b) => {
        const aInfo = columnInfo.find(col => col.columnKey === a.id);
        const bInfo = columnInfo.find(col => col.columnKey === b.id);

        const aPriority = aInfo?.priority ?? 999;
        const bPriority = bInfo?.priority ?? 999;

        return aPriority - bPriority;
      });

      result = [...(checkboxCol ? [checkboxCol] : []), ...columnsToSort, ...(actionsCol ? [actionsCol] : [])];
    }

    return result;
  };

  const filteredColumns = getVisibleColumns();
  const leftFixedColumns = filteredColumns.filter(col => col.fixed === 'left');
  const rightFixedColumns = filteredColumns.filter(col => col.fixed === 'right');
  const scrollableColumns = filteredColumns.filter(col => !col.fixed);

  const getChildRowColSpan = () =>
    leftFixedColumns.length + (scrollableColumns.length > 0 ? 1 : 0) + rightFixedColumns.length;

  // Calculate cumulative left positions for fixed columns
  const getLeftPosition = (index: number) => {
    let position = 0;
    for (let i = 0; i < index; i++) {
      const col = leftFixedColumns[i];
      // Extract width value from Tailwind class (e.g., 'w-[50px]' -> 50)
      const widthMatch = col.width?.match(/w-\[(\d+)px\]/);
      if (widthMatch) {
        position += parseInt(widthMatch[1]);
      }
    }
    return position;
  };

  // Calculate minimum table width based on all column widths
  const getMinTableWidth = () => {
    let totalWidth = 0;
    filteredColumns.forEach(col => {
      const widthMatch = col.width?.match(/w-\[(\d+)px\]/);
      if (widthMatch) {
        totalWidth += parseInt(widthMatch[1]);
      } else {
        // Default width for columns without explicit width
        totalWidth += 150;
      }
    });
    return totalWidth;
  };

  const minTableWidth = getMinTableWidth();

  const isSortable = (columnId: string): boolean => {
    const column = columns.find(col => col.id === columnId);
    return column?.sortable && !!sorting?.onSortChange;
  };

  const handleSortClick = (columnId: string) => {
    if (!sorting?.onSortChange || !isSortable(columnId)) return;

    const enableMultiSort = sorting.enableMultiSort ?? false;
    const currentSortBy = sorting.sortBy || '';
    const currentSortOrder = sorting.sortOrder || '';

    if (enableMultiSort) {
      // Multi-column sorting logic
      // Parse comma-separated strings
      const sortByArr = currentSortBy ? currentSortBy.split(',') : [];
      const sortOrderArr = currentSortOrder ? currentSortOrder.split(',') : [];

      // Find if column already exists in sort list
      const existingIndex = sortByArr.findIndex(col => col === columnId);

      let newSortByArr: string[];
      let newSortOrderArr: string[];

      if (existingIndex !== -1) {
        // Column exists - update its sort order
        newSortByArr = [...sortByArr];
        newSortOrderArr = [...sortOrderArr];

        const currentOrder = sortOrderArr[existingIndex]?.toLowerCase();
        // Transition: asc -> desc, desc -> asc
        if (currentOrder === 'asc') {
          newSortOrderArr[existingIndex] = 'desc';
        } else {
          newSortOrderArr[existingIndex] = 'asc';
        }
      } else {
        // Column doesn't exist - add as 'asc' (first click)
        newSortByArr = [...sortByArr, columnId];
        newSortOrderArr = [...sortOrderArr, 'asc'];
      }

      // Reconstruct comma-separated strings
      const newSortBy = newSortByArr.join(',');
      const newSortOrder = newSortOrderArr.join(',');

      sorting.onSortChange(newSortBy, newSortOrder);
    } else {
      // Single column sorting logic
      if (currentSortBy === columnId) {
        // Same column clicked - toggle sort order
        const currentOrder = currentSortOrder.toLowerCase();
        const newOrder = currentOrder === 'asc' ? 'desc' : 'asc';
        sorting.onSortChange(columnId, newOrder);
      } else {
        // Different column clicked - set as new sort (asc on first click)
        sorting.onSortChange(columnId, 'asc');
      }
    }
  };

  const getSortingProps = (columnId: string) => {
    const enableMultiSort = sorting?.enableMultiSort ?? false;
    const currentSortBy = sorting?.sortBy || '';
    const currentSortOrder = sorting?.sortOrder || '';

    let isActiveSort: boolean;
    let columnSortOrder: string;

    if (enableMultiSort) {
      // Multi-column sorting - parse comma-separated strings to find this column's individual sort state
      const sortByArr = currentSortBy ? currentSortBy.split(',') : [];
      const sortOrderArr = currentSortOrder ? currentSortOrder.split(',') : [];
      const existingIndex = sortByArr.findIndex(col => col === columnId);

      // Determine if this column is active and its current order
      isActiveSort = existingIndex !== -1;
      columnSortOrder = isActiveSort ? (sortOrderArr[existingIndex] || 'asc').toLowerCase() : 'desc';
    } else {
      // Single column sorting - check if this is the active column
      isActiveSort = currentSortBy === columnId;
      columnSortOrder = isActiveSort ? (currentSortOrder || 'asc').toLowerCase() : 'desc';
    }

    const shouldShowIcon = isActiveSort || hoveredColumn === columnId;
    const isColumnSortable = isSortable(columnId);

    let tooltipText = '';
    if (isColumnSortable) {
      const isTextColumn = textColumns.includes(columnId);
      // Pass the column's individual sort state to maintain tooltip behavior
      // Tooltip function handles both uppercase and lowercase
      const tooltipSortOrder = isActiveSort ? columnSortOrder : 'desc';
      tooltipText = getSortTooltipText(columnId, isActiveSort ? columnId : '', tooltipSortOrder, isTextColumn);
    }

    return { tooltipText, shouldShowIcon, isActiveSort, isColumnSortable, columnSortOrder };
  };

  const renderSortIcon = (
    isActiveSort: boolean,
    shouldShowIcon: boolean,
    isColumnSortable: boolean,
    columnSortOrder?: string
  ) => {
    if (!isColumnSortable) return null;
    const isDesc = columnSortOrder?.toLowerCase() === 'desc';
    return (
      <div className="ml-1 cursor-pointer rounded-md py-1 px-1.5 hover:bg-gray-200">
        <SortByIcon
          className={`path path-fill-current rotate-180 transition-transform duration-200
              ${shouldShowIcon ? 'opacity-100' : 'opacity-0'} 
              ${isActiveSort ? 'text-primaryText' : 'text-accentText'} 
              ${isDesc && isActiveSort ? 'rotate-0' : ''}`}
        />
      </div>
    );
  };

  const handleRowClick = (item: T, event: React.MouseEvent) => {
    if (onRowClick) {
      onRowClick(item, event);
    }
  };

  const getRowIdForItem = (item: T, rowIndex: number): string => {
    if (selection?.getItemId) {
      return selection.getItemId(item);
    }
    return String(rowIndex);
  };

  const isRowHighlighted = (rowId: string): boolean => {
    return highlightedRowId !== undefined && highlightedRowId !== null && rowId === highlightedRowId;
  };

  if (loading) {
    return (
      <div className={`w-full ${className}`}>
        {loadingComponent || (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
              <p className="mt-2 text-sm text-gray-500">Loading...</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  const showEmptyState = !rowGroups && !data.length;
  if (showEmptyState) {
    return (
      <div className={`w-full ${className}`}>
        {emptyState || (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <p className="text-gray-500">No data available</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <div className={classNames('relative isolate border border-gray-200', wrapperClassName)}>
        <div
          ref={tableWrapperRef}
          className={classNames('overflow-x-auto', {
            'scrollbar-hide-x': !!stickyHorizontalScrollbar
          })}
          style={{
            ...(tableMinHeight === 'auto' && { minHeight: 'auto' }),
            ...(tableMinHeight !== 'auto' && {
              maxHeight: tableMinHeight,
              overflowY: 'auto'
            })
          }}>
          <table className={`w-full ${tableClassName}`} style={{ minWidth: `${minTableWidth}px` }}>
            <thead ref={tableHeaderRef} className="sticky top-0 z-30 bg-[#F4F4F4]">
              <tr className="bg-[#F4F4F4]">
                {/* Left Fixed Columns */}
                {leftFixedColumns.map((column, index) => {
                  const { tooltipText, shouldShowIcon, isActiveSort, isColumnSortable, columnSortOrder } =
                    getSortingProps(column.id);
                  const leftPosition = getLeftPosition(index);
                  return (
                    <th
                      key={column.id}
                      className={`${column.width || 'w-auto'} sticky z-20 whitespace-nowrap 
                      bg-[#F4F4F4] px-4 py-3 text-left text-sbase font-semibold text-primaryText first-of-type:pr-0
                       ${index === leftFixedColumns.length - 1 ? leftFixedColumnSeperatorStyles : ''}`}
                      style={{ left: `${leftPosition}px` }}
                      onMouseEnter={() => setHoveredColumn(column.id)}
                      onMouseLeave={() => setHoveredColumn(null)}>
                      <TooltipWrapper
                        tooltip={tooltipText}
                        position="bottom"
                        showTooltip={isColumnSortable}
                        className="w-fit">
                        <div
                          className={classNames('flex max-w-fit items-center rounded-md', {
                            'hover:bg-gray-200 cursor-pointer': isColumnSortable
                          })}
                          onClick={() => isColumnSortable && handleSortClick(column.id)}>
                          {typeof column.header === 'function'
                            ? column.header({
                                selectedItems: selection?.selectedItems,
                                allItems: data,
                                onSelectAll: selection?.onSelectAll
                              })
                            : column.header}
                          {renderSortIcon(isActiveSort, shouldShowIcon, isColumnSortable, columnSortOrder)}
                        </div>
                      </TooltipWrapper>
                    </th>
                  );
                })}

                {/* Scrollable Columns */}
                <th className="bg-[#F4F4F4] p-0">
                  <div className="flex min-w-max flex-row">
                    {scrollableColumns.map(column => {
                      const { tooltipText, shouldShowIcon, isActiveSort, isColumnSortable, columnSortOrder } =
                        getSortingProps(column.id);
                      return (
                        <div
                          key={column.id}
                          className={classNames(
                            `${column.width || 'w-auto'} whitespace-nowrap px-4 py-3 
                            text-left text-sbase font-semibold text-primaryText`,
                            column.tooltip && 'flex items-center'
                          )}
                          onMouseEnter={() => setHoveredColumn(column.id)}
                          onMouseLeave={() => setHoveredColumn(null)}>
                          <TooltipWrapper
                            tooltip={tooltipText}
                            position="bottom"
                            showTooltip={isColumnSortable || false}
                            className="w-fit">
                            <div
                              className={classNames('flex max-w-fit items-center rounded-md pl-1', {
                                'hover:bg-gray-200 cursor-pointer': isColumnSortable
                              })}
                              onClick={() => isColumnSortable && handleSortClick(column.id)}>
                              {typeof column.header === 'function'
                                ? column.header({
                                    selectedItems: selection?.selectedItems,
                                    allItems: data,
                                    onSelectAll: selection?.onSelectAll
                                  })
                                : column.header}
                              {column.tooltip && (
                                <div
                                  onMouseEnter={() => setHoveredTooltipId(column.id)}
                                  onMouseLeave={() => setHoveredTooltipId(null)}>
                                  <TooltipWrapper
                                    tooltip={column.tooltip}
                                    className='ml-1 cursor-pointer [&>[role="tooltip"]]:z-50'
                                    showTooltip={hoveredTooltipId === column.id}
                                    position="bottom">
                                    <GreyStrokeInfoIcon className="h-4 w-4" />
                                  </TooltipWrapper>
                                </div>
                              )}
                              {renderSortIcon(isActiveSort, shouldShowIcon, isColumnSortable, columnSortOrder)}
                            </div>
                          </TooltipWrapper>
                        </div>
                      );
                    })}
                  </div>
                </th>

                {/* Right Fixed Columns */}
                {rightFixedColumns.map(column => {
                  const { tooltipText, shouldShowIcon, isActiveSort, isColumnSortable, columnSortOrder } =
                    getSortingProps(column.id);
                  return (
                    <th
                      key={column.id}
                      className={`${column.width || 'w-auto'} sticky right-0 z-20 whitespace-nowrap
                      bg-[#F4F4F4] px-4 py-3 text-left text-sbase font-semibold text-primaryText
                       ${rightFixedColumnSeperatorStyles}`}
                      onMouseEnter={() => setHoveredColumn(column.id)}
                      onMouseLeave={() => setHoveredColumn(null)}>
                      <TooltipWrapper
                        tooltip={tooltipText}
                        position="bottom"
                        showTooltip={isColumnSortable || false}
                        className="w-fit">
                        <div
                          className={classNames('flex max-w-fit items-center rounded-md', {
                            'hover:bg-gray-200 cursor-pointer': isColumnSortable
                          })}
                          onClick={() => isColumnSortable && handleSortClick(column.id)}>
                          {typeof column.header === 'function'
                            ? column.header({
                                selectedItems: selection?.selectedItems,
                                allItems: data,
                                onSelectAll: selection?.onSelectAll
                              })
                            : column.header}
                          {renderSortIcon(isActiveSort, shouldShowIcon, isColumnSortable, columnSortOrder)}
                        </div>
                      </TooltipWrapper>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody
              className={
                'divide-y divide-gray-200' +
                ' [&_.child-row-spacer]:border-none [&_.child-row-body]:border-none [&_.child-row-footer]:border-none'
              }>
              {(() => {
                let dataRowIndex = 0;

                const renderChildDataRow = (childRow: TableChildRow<T>, isLastChildOfParent: boolean) => {
                  const childItem = childRow.item as T;
                  const rowContext = {
                    isChildRow: true,
                    rowVariant: childRow.variant,
                    parentItem: childRow.parentItem
                  };
                  const defaultChildBgClass = getChildRowBackgroundClass(childRow.variant, false);
                  const customRowClassName = childRows?.getChildRowClassName?.(childRow.variant) ?? '';
                  const showSpacerBefore = childRows?.showSpacerBeforeChild !== false;
                  const showGroupFooter = childRows?.showGroupFooterAfterLastChild !== false && isLastChildOfParent;

                  const leftCount = leftFixedColumns.length;
                  const scrollCount = scrollableColumns.length;
                  const rightCount = rightFixedColumns.length;
                  const totalCells = leftCount + scrollCount + rightCount;

                  const getCellClassName = (
                    columnId: string,
                    section: 'left' | 'scroll' | 'right',
                    cellIndex: number
                  ) => {
                    const isFirstCell = cellIndex === 0;
                    const isLastCell = cellIndex === totalCells - 1;
                    const customCellClassName = childRows?.getChildCellClassName?.({
                      variant: childRow.variant,
                      columnId,
                      section,
                      isFirstCell,
                      isLastCell
                    });

                    if (customCellClassName) {
                      return customCellClassName;
                    }

                    return defaultChildBgClass;
                  };

                  let cellIndex = 0;

                  return (
                    <Fragment key={childRow.rowId}>
                      {showSpacerBefore && (
                        <tr className="child-row-spacer">
                          <td colSpan={getChildRowColSpan()} className="py-0.5" />
                        </tr>
                      )}
                      <tr
                        onClick={e => childRows?.onChildRowClick?.(childRow, e)}
                        className={classNames(
                          'child-row-body group',
                          customRowClassName || 'remove-highlight cursor-pointer align-top [&>*]:p-2',
                          {
                            'cursor-pointer': !!childRows?.onChildRowClick
                          }
                        )}>
                        {leftFixedColumns.map((column, index) => {
                          const leftPosition = getLeftPosition(index);
                          const currentCellIndex = cellIndex;
                          cellIndex += 1;
                          return (
                            <td
                              key={column.id}
                              className={classNames(
                                `${column.width || 'w-auto'} sticky z-20 p-4 align-top text-sbase text-primaryText`,
                                getCellClassName(column.id, 'left', currentCellIndex),
                                {
                                  [leftFixedColumnSeperatorStyles]: index === leftFixedColumns.length - 1
                                }
                              )}
                              style={{ left: `${leftPosition}px` }}>
                              {formatCellValue(childItem, column.accessor, rowContext)}
                            </td>
                          );
                        })}

                        <td className="relative p-0">
                          <div className="inline-flex min-w-max">
                            {scrollableColumns.map(column => {
                              const currentCellIndex = cellIndex;
                              cellIndex += 1;
                              return (
                                <div
                                  key={column.id}
                                  className={classNames(
                                    `${column.width || 'w-auto'} p-4 pl-3 text-sbase text-primaryText`,
                                    getCellClassName(column.id, 'scroll', currentCellIndex)
                                  )}>
                                  {formatCellValue(childItem, column.accessor, rowContext)}
                                </div>
                              );
                            })}
                          </div>
                        </td>

                        {rightFixedColumns.map(column => {
                          const currentCellIndex = cellIndex;
                          cellIndex += 1;
                          return (
                            <td
                              key={column.id}
                              className={classNames(
                                `${column.width || 'w-auto'} sticky right-0 z-20 align-top text-sbase text-primaryText`,
                                getCellClassName(column.id, 'right', currentCellIndex),
                                rightFixedColumnSeperatorStyles
                              )}>
                              {formatCellValue(childItem, column.accessor, rowContext)}
                            </td>
                          );
                        })}
                      </tr>
                      {showGroupFooter && (
                        <tr className="child-row-footer">
                          <td colSpan={getChildRowColSpan()} className="border-b border-secondaryBtn pt-4" />
                        </tr>
                      )}
                    </Fragment>
                  );
                };

                const renderDataRow = (item: T) => {
                  const rowIndex = dataRowIndex;
                  dataRowIndex += 1;
                  const itemId = getRowIdForItem(item, rowIndex);
                  const isHighlighted = isRowHighlighted(itemId);
                  const nestedChildRows = childRows?.getChildRows?.(item) ?? [];

                  return (
                    <Fragment key={itemId}>
                      <tr
                        onClick={e => handleRowClick(item, e)}
                        className={classNames('group ', {
                          'cursor-pointer': !!onRowClick,
                          'hover:bg-gray-50': enableRowHover && !isHighlighted,
                          'bg-secondaryBtn': isHighlighted,
                          'border-b border-secondaryBtn': nestedChildRows.length === 0
                        })}>
                        {leftFixedColumns.map((column, index) => {
                          const leftPosition = getLeftPosition(index);
                          return (
                            <td
                              key={column.id}
                              className={classNames(
                                `${column.width || 'w-auto'} sticky bg-white z-20 p-4 
                          text-sbase font-semibold text-primaryText first-of-type:pr-0 align-top`,
                                {
                                  'bg-secondaryBtn': isHighlighted,
                                  'group-hover:bg-gray-50': enableRowHover && !isHighlighted,
                                  [leftFixedColumnSeperatorStyles]: index === leftFixedColumns.length - 1,
                                  'border-t-2 border-gray-200': rowIndex !== 0
                                }
                              )}
                              style={{ left: `${leftPosition}px` }}>
                              {formatCellValue(item, column.accessor)}
                            </td>
                          );
                        })}

                        <td
                          className={classNames(' bg-white p-0 relative', {
                            'bg-secondaryBtn': isHighlighted,
                            'group-hover:bg-gray-50': enableRowHover && !isHighlighted
                          })}>
                          <div className="inline-flex min-w-max">
                            {scrollableColumns.map(column => (
                              <div
                                key={column.id}
                                onClick={column.id === 'actions' ? e => e.stopPropagation() : undefined}
                                className={classNames(
                                  `${column.width || 'w-auto'} p-4 pl-5 text-sbase font-semibold text-primaryText`,
                                  { 'group-hover:bg-gray-50': enableRowHover && !isHighlighted }
                                )}>
                                {formatCellValue(item, column.accessor)}
                              </div>
                            ))}
                          </div>
                        </td>

                        {rightFixedColumns.map(column => (
                          <td
                            key={column.id}
                            onClick={e => e.stopPropagation()}
                            className={classNames(
                              `${column.width || 'w-auto'} sticky right-0 z-20 bg-white p-4 
                        text-sbase font-semibold text-primaryText`,
                              rightFixedColumnSeperatorStyles,
                              {
                                'bg-secondaryBtn': isHighlighted,
                                'group-hover:bg-gray-50': enableRowHover && !isHighlighted,
                                'border-t-2 border-gray-200': rowIndex !== 0
                              }
                            )}>
                            {formatCellValue(item, column.accessor)}
                          </td>
                        ))}
                      </tr>
                      {nestedChildRows.map(
                        (childRow, childIndex) =>
                          renderChildDataRow(childRow, childIndex === nestedChildRows.length - 1)
                        // eslint-disable-next-line function-paren-newline
                      )}
                    </Fragment>
                  );
                };

                if (rowGroups) {
                  return rowGroups.groups.map(({ key, items }) => (
                    <Fragment key={key}>
                      <tr className="border-b border-gray-200" style={rowGroups.groupHeaderRowStyle?.(key, items)}>
                        <td
                          colSpan={leftFixedColumns.length + 1}
                          className="py-1.5 px-4 pl-5 text-left text-sbase font-semibold text-primaryText">
                          {rowGroups.renderGroupHeader(key, items)}
                        </td>
                        {rightFixedColumns.map(column => (
                          <td
                            key={column.id}
                            className={classNames(
                              `${column.width || 'w-auto'} sticky 
                                right-0 z-10 border-l border-gray-200 bg-[#e9ecf2] p-0`,
                              rightFixedColumnSeperatorStyles
                            )}
                          />
                        ))}
                      </tr>
                      {items.map(item => renderDataRow(item))}
                    </Fragment>
                  ));
                }

                return data.map(item => renderDataRow(item));
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {stickyHorizontalScrollbar && hasHorizontalOverflow && (
        <div
          ref={stickyScrollbarRef}
          role="scrollbar"
          aria-label="Horizontal scroll for table"
          tabIndex={0}
          className={classNames(
            'stickyTableScrollbar fixed left-0 z-[9] h-3 overflow-x-scroll',
            'overflow-y-hidden border-t border-borderGray bg-[#F4F4F4] opacity-70',
            'transition-opacity hover:opacity-100',
            isStickyScrollbarHovered && 'stickyTableScrollbar--trackHovered'
          )}
          style={{
            bottom: `${stickyHorizontalScrollbar.bottomOffsetPx}px`,
            left: scrollbarRect?.left ?? 0,
            width: scrollbarRect?.width ?? '100%'
          }}
          onMouseEnter={() => setIsStickyScrollbarHovered(true)}
          onMouseLeave={() => setIsStickyScrollbarHovered(false)}
          onKeyDown={e => {
            const step = 40;
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              stickyScrollbarRef.current && (stickyScrollbarRef.current.scrollLeft -= step);
            }
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              stickyScrollbarRef.current && (stickyScrollbarRef.current.scrollLeft += step);
            }
          }}>
          <div style={{ minWidth: `${minTableWidth}px`, height: 1 }} />
        </div>
      )}

      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalCount={pagination.totalCount}
          onPageChange={pagination.onPageChange}
          limit={pagination.limit || 50}
          wrapperClassName={pagination.wrapperClassName}
        />
      )}
    </div>
  );
};

export default GenericTable;
